import React, {useReducer} from "react";
import {useFormik} from "formik";
import * as yup from "yup";
import {formatISO, startOfDay} from "date-fns";

import SettingsPanel from "./SettingsPanel";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import {useCreatePricesMutation} from "../query";
import {BaseResponsiveDialog} from "../../../components/ResponsiveDialog/BaseResponsiveDialog";
import {shortString} from "../../../services/date/formatters";
import {submitDisabled } from "../../../services/formik/utils";

import PricePanel from "./PricePanel";
import Typography from "@mui/material/Typography";
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {useMainLayout} from "../../../layouts/Main";
import Button from "@mui/material/Button";
import {getLastDateToPlan} from "../index";
import {isValidDateObject} from "../../../services/date/validators";


const validDateSchema = yup.date()
    .required('Campo obbligatorio')
    .typeError('Deve essere una data valida')

const positiveNumberSchema = yup.number()
    .required('Campo obbligatorio')
    .positive('Deve essere un numero positivo')
    .integer('Deve essere un numero intero')
    .typeError('Deve essere un numero')

const FormConfigurator = (props) => {
    const {
        rate,
        apartmentId,
        onClose,
        dateFrom,
        dateTo,
        useSeasonalConditions,
        priceCentMin,
        priceCentMax,
    } = props;

    const seasonValidationSchema = yup.object().shape({
        minNights: positiveNumberSchema,
        minPeople: positiveNumberSchema
            .min(rate.minPeople, `Deve essere maggiore o uguale a ${rate.minPeople} (capacità minima tariffa)`)
            .max(rate.maxPeople, `Deve essere minore o uguale a ${rate.minPeople} (capacità massima tariffa)`),
    })

    const [conditions, toggleConditions] = useReducer(state => !state, false);

    const {showErrorNotification} = useFeedbackTools();
    const mutation = useCreatePricesMutation({apartmentId, rateId: rate.id})

    const maxDateTo = getLastDateToPlan();

    const formik = useFormik({
        initialValues: {
            dateFrom: dateFrom,
            dateTo: dateTo,
            minPriceCent: priceCentMin,
            maxPriceCent: priceCentMax,

            useSeasonalConditions: useSeasonalConditions,

            highSeason: {
                minPeople: rate.basePeople,
                minNights: 7,
                checkin: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: true,
                    sunday: false,
                },
                checkout: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: true,
                    sunday: false,
                }
            },
            midSeason: {
                minPeople: Math.max(Math.floor(rate.basePeople / 2), rate.minPeople),
                minNights: 5,
                checkin: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: true,
                    sunday: true,
                },
                checkout: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: true,
                    friday: true,
                    saturday: true,
                    sunday: false,
                }
            },
            lowSeason: {
                minPeople: Math.max(Math.floor(rate.basePeople / 2), rate.minPeople),
                minNights: 3,
                checkin: {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: true,
                    sunday: true,
                },
                checkout: {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: true,
                    sunday: true,
                }
            },
        },

        validationSchema: yup.object({
            dateFrom: validDateSchema,
            dateTo: validDateSchema.max(maxDateTo, `Deve essere entro il ${shortString(maxDateTo)}`).when('dateFrom', (dateFrom, schema) => {
                if (!isValidDateObject(dateFrom)) return schema;

                const max = new Date(dateFrom);
                max.setDate(max.getDate() + 366);

                return schema.max(
                    max,
                    `Deve essere entro il ${shortString(max)} (max 1 anno di programmazione)`
                );
            }),
            minPriceCent: positiveNumberSchema,
            maxPriceCent: positiveNumberSchema,
            highSeason: seasonValidationSchema,
            midSeason: seasonValidationSchema,
            lowSeason: seasonValidationSchema,
        }),
        onSubmit: async (values) => {
            const {dateFrom, dateTo, ...rest} = values;
            try{
                await mutation.mutateAsync({
                    dateFrom: formatISO(dateFrom, {representation: 'date'}),
                    dateTo: formatISO(dateTo, {representation: 'date'}),
                    ...rest,
                });
                onClose();
            }catch (e) {
                showErrorNotification(e);
            }
        }
    })

    return <BaseResponsiveDialog headerEl={<Title conditions={conditions}
                                                  toggleConditions={toggleConditions}
                                                  onClose={onClose}
                                                  onApply={formik.handleSubmit}/>}
                                 actionsEl={<Actions onClose={onClose}
                                                     onApply={formik.handleSubmit}
                                                     applyDisabled={submitDisabled(formik)}/>}
                                 open={true}
                                 onClose={onClose}
                                 isOpen
                                 isLoading={mutation.isLoading}>
        {!conditions ? <PricePanel formik={formik}/> : <SettingsPanel formik={formik}/>}
    </BaseResponsiveDialog>
}

const Title = ({conditions, toggleConditions, onClose, onApply}) => {
    const {isMobile} = useMainLayout();

    if (!conditions) {
        return <>
            {isMobile && <IconButton size="small" onClick={onClose} color={'inherit'}><CloseIcon/></IconButton>}
            <Typography sx={{flexGrow: 1}} fontWeight={500} fontSize="1.25rem">Configura prezzi</Typography>
            <IconButton size="small" onClick={toggleConditions} color={'inherit'}>
                <SettingsIcon/>
            </IconButton>
            {isMobile && <Button autoFocus color="inherit" onClick={onApply}>Salva</Button>}
        </>
    } else {
        return <>
            <IconButton size="small" onClick={toggleConditions} color={'inherit'}><ArrowBackIcon/></IconButton>
            <Typography variant="h6" component="span" sx={{flexGrow: 1}}>Configura condizioni</Typography>
        </>
    }
}

const Actions = ({onClose, onApply, applyDisabled}) => {
    const {isMobile} = useMainLayout();

    if (isMobile) {
        return null;
    }

    return <>
        <Button color="primary" variant="text" onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={onApply} disabled={applyDisabled}>Salva</Button>
    </>
}


// Set defaults
FormConfigurator.defaultProps = {
    dateFrom: startOfDay(new Date()),
    dateTo: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    useSeasonalConditions: true,
    priceCentMin: 3000,
    priceCentMax: 7500,
}


export default FormConfigurator;